<!-- Scrolled by the nav's content slot (see CLAUDE.md "Layout & scrolling"); min-h-full + flex-col
     only lets the empty/loading state fill and centre without creating a second scroller. -->
<div class="min-h-full flex flex-col">
  <!-- Filters: what (free lodges, a room to share, then each type) and a state - the same dropdowns as /sales,
       half the row each on a phone. Each option says how many houses it shows (given the other filter), and
       empty ones are left out. Kept in the URL (?type=&state=). compact-input-theme: custom-theme.scss.
       Solid white and z-20: the cards' photos and buttons are z-10, so a lower or see-through bar lets
       them show through it, or over it, as the list scrolls under. -->
  <div *ngIf="hasAnyHouses$ | async" class="sticky top-0 z-20 border-b border-black/5 bg-white">
    <ng-container *ngIf="filters$ | async as filters">
      <div class="grid grid-cols-2 gap-2 px-3 pt-2 sm:flex sm:px-5">
        <mat-form-field appearance="outline" subscriptSizing="dynamic" class="compact-input-theme min-w-0 sm:w-64">
          <mat-select
            aria-label="What"
            placeholder="All houses"
            panelClass="filter-select-panel filter-select-panel-wide"
            [value]="what$ | async"
            (selectionChange)="setWhat($event.value || null)"
          >
            <mat-select-trigger>{{ whatName(what$ | async) }}</mat-select-trigger>
            <mat-option [value]="null"><span>All houses</span> <span class="count">{{ filters.allWhat }}</span></mat-option>
            <mat-option *ngFor="let option of filters.special" [value]="option.value">
              <span>{{ option.label }}</span> <span class="count">{{ option.count }}</span>
            </mat-option>
            <mat-optgroup *ngIf="filters.types.length" label="By type">
              <mat-option *ngFor="let option of filters.types" [value]="option.value">
                <span>{{ option.label }}</span> <span class="count">{{ option.count }}</span>
              </mat-option>
            </mat-optgroup>
          </mat-select>
        </mat-form-field>

        <mat-form-field appearance="outline" subscriptSizing="dynamic" class="compact-input-theme min-w-0 sm:w-56">
          <mat-select
            aria-label="State"
            placeholder="All states"
            panelClass="filter-select-panel"
            [value]="state$ | async"
            (selectionChange)="setState($event.value || null)"
          >
            <mat-select-trigger>{{ (state$ | async) ?? 'All states' }}</mat-select-trigger>
            <mat-option [value]="null"><span>All states</span> <span class="count">{{ filters.allStates }}</span></mat-option>
            <mat-option *ngFor="let state of filters.states" [value]="state.value">
              <span>{{ state.value }}</span> <span class="count">{{ state.count }}</span>
            </mat-option>
          </mat-select>
        </mat-form-field>
      </div>

      <p *ngIf="shown$ | async as shown" class="m-0 flex items-center gap-3 px-3 py-2 text-sm text-gray-600 sm:px-5" aria-live="polite">
        {{ shown.length }} {{ shown.length === 1 ? 'house' : 'houses' }}
        <!-- With nothing to show, the empty state below has its own "Clear filters" -->
        <button *ngIf="filters.filtered && shown.length" type="button" (click)="clearFilters()"
          class="rounded font-medium text-primary underline hover:text-primary-light focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary">
          Clear filters
        </button>
      </p>
    </ng-container>
  </div>

  <ng-container *ngIf="shown$ | async as shown">
    <div *ngIf="shown.length; else empty" class="house-grid p-3 pb-28 sm:p-5 sm:pb-28">
      <app-feed-content *ngFor="let house of shown; trackBy: trackById" [post]="house"></app-feed-content>
    </div>
  </ng-container>

  <ng-template #empty>
    <div *ngIf="isFetching; else nothingHere" class="flex flex-1 items-center justify-center p-5">
      <mat-spinner mode="indeterminate" aria-label="Loading houses"></mat-spinner>
    </div>

    <ng-template #nothingHere>
      <div class="flex flex-1 flex-col items-center justify-center gap-3 px-6 py-16 text-center">
        <mat-icon class="!h-12 !w-12 !text-5xl text-primary/30" aria-hidden="true">apartment</mat-icon>

        <ng-container *ngIf="hasAnyHouses$ | async; else noHousesAtAll">
          <p class="m-0 text-base font-medium text-gray-900">Nothing matches that yet.</p>
          <p class="m-0 max-w-sm text-sm text-gray-500">Try another kind of house or state - or tell us what you're looking for, and we'll let you know when someone lists it.</p>
          <div class="flex flex-wrap justify-center gap-2 pt-2">
            <button type="button" mat-stroked-button color="primary" (click)="clearFilters()">Clear filters</button>
            <button type="button" mat-flat-button color="primary" (click)="openWishlist()">Add to my wishlist</button>
          </div>
        </ng-container>

        <ng-template #noHousesAtAll>
          <p class="m-0 text-base font-medium text-gray-900">No houses listed yet.</p>
          <p class="m-0 max-w-sm text-sm text-gray-500">Leaving your place soon? Be the first to list it. Looking for one? We'll let you know when someone does.</p>
          <div class="flex flex-wrap justify-center gap-2 pt-2">
            <button type="button" mat-flat-button color="primary" (click)="callerService.openCreateAccommodationPostDialog()">List a house</button>
            <button type="button" mat-stroked-button color="primary" (click)="openWishlist()">Add to my wishlist</button>
          </div>
        </ng-template>
      </div>
    </ng-template>
  </ng-template>

  <!-- Like /sales' "Sell": a labelled button, not an icon that only says what it does on hover -->
  <button
    type="button"
    (click)="callerService.openCreateAccommodationPostDialog()"
    class="fixed bottom-16 right-4 z-10 flex items-center gap-1.5 rounded-full bg-primary py-3 pl-4 pr-5 font-semibold text-white shadow-card-elevated transition hover:bg-primary-light hover:shadow-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary md:bottom-8 md:right-8"
  >
    <mat-icon aria-hidden="true">domain_add</mat-icon>
    List a house
  </button>
</div>
